import { FC, MouseEvent, memo, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { AxiosError } from 'axios';
import toastService from 'components/elements/Toastify';
import ConfirmationModal from 'components/modules/ConfirmationModal';
import { IErrorResponse } from 'types';
import { getErrorMessage } from 'utils/helpers';

interface RejectedDataProcessButtonProps {
	confirmMessage: string;
	confirmTitle: string;
	disabled?: boolean;
	infoMessage: string;
	label?: string;
	onProcessSuccess?: () => void;
	onProcess: () => Promise<unknown>;
}

export const RejectedDataProcessButton: FC<RejectedDataProcessButtonProps> = memo(
	({ confirmMessage, confirmTitle, disabled = false, infoMessage, label = 'Запустити обробку', onProcess, onProcessSuccess }) => {
		const [isLoading, setIsLoading] = useState(false);
		const [isModalOpen, setIsModalOpen] = useState(false);

		const handleOpenModal = (event: MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			setIsModalOpen(true);
		};

		const handleCloseModal = () => {
			if (!isLoading) {
				setIsModalOpen(false);
			}
		};

		const handleConfirm = async () => {
			try {
				setIsLoading(true);
				await onProcess();
				setIsModalOpen(false);
				onProcessSuccess?.();
				toastService.info(infoMessage);
			} catch (error) {
				toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
			} finally {
				setIsLoading(false);
			}
		};

		return (
			<>
				<Button
					variant="outlined"
					size="small"
					disabled={disabled || isLoading}
					onClick={handleOpenModal}
					sx={{ minWidth: 190, textTransform: 'none' }}
				>
					{isLoading ? (
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							<CircularProgress size={18} color="inherit" />
						</Box>
					) : (
						label
					)}
				</Button>
				<ConfirmationModal open={isModalOpen} onClose={handleCloseModal} title={confirmTitle} message={confirmMessage}>
					<Box sx={{ display: 'flex', gap: 1.5 }}>
						<Button variant="contained" color="primary" onClick={handleCloseModal} disabled={isLoading}>
							Скасувати
						</Button>
						<Button
							variant="contained"
							color="secondary"
							onClick={() => {
								void handleConfirm();
							}}
							disabled={isLoading}
						>
							{isLoading ? <CircularProgress size={18} color="inherit" /> : 'Підтвердити'}
						</Button>
					</Box>
				</ConfirmationModal>
			</>
		);
	}
);
